<template>
    <div
        v-if="typeof _outerFixedPosition === 'number'"
        v-show="isInFixed && isWrapperIntersecting"
        ref="fixedWrapperRef"
        :class="[$style.outerFixedWrapper, fixedWrapperClass]"
        :style="{ ...fixedWrapperStyle, [fixedDirectionMain]: `${_outerFixedPosition}px` }"
    >
        <div :class="navClass" :style="{ ...navStyle, [fixedKey]: `${rect[fixedKey]}px` }">
            <slot></slot>
        </div>
    </div>

    <div
        v-if="typeof _outerFixedPosition === 'number' && uniqueKey === 'header'"
        ref="shepherd"
        :class="[$style.shepherd, $style[horizontal ? 'horizontal' : 'vertical']]"
        :style="{ [fixedDirectionMain]: `-${_outerFixedPosition}px` }"
    />

    <div
        v-if="!innerFixed"
        ref="slotRef"
        :class="[navClass, $style.nav, { [$style.isInFixed]: isInFixed }]"
        :style="navStyle"
    >
        <slot></slot>
    </div>
    <div
        v-else
        ref="fixedWrapperRef"
        :class="[$style.innerFixedWrapper, fixedWrapperClass]"
        :style="{ ...fixedWrapperStyle, [fixedDirectionMain]: `${_outerFixedPosition}px` }"
    >
        <div ref="slotRef" :class="navClass" :style="navStyle">
            <slot></slot>
        </div>
    </div>

    <div
        v-if="typeof _outerFixedPosition === 'number' && uniqueKey === 'footer'"
        ref="shepherd"
        :class="[$style.shepherd, $style[horizontal ? 'horizontal' : 'vertical']]"
        :style="{ [fixedDirectionMain]: `-${_outerFixedPosition}px` }"
    />
</template>

<script lang="ts">
export default {
    name: 'NavSlot',
};
</script>

<script lang="ts" setup>
import { ref, watch, inject, computed, onUnmounted, nextTick } from 'vue';
import type { CSSProperties, Ref } from 'vue';
import clamp from 'lodash-es/clamp.js';
import useMeasure from '../../hooks/useMeasure';

interface Props {
    horizontal: boolean;
    uniqueKey: 'header' | 'footer';
    extraProps?: Record<string, any>;
    navClass?: string;
    navStyle?: CSSProperties;
    fixedWrapperClass?: string;
    fixedWrapperStyle?: CSSProperties;
    outerFixedPosition?: number | boolean;
    innerFixed?: boolean;
    wrapperRect: {
        width: number;
        height: number;
        left: number;
        top: number;
    };
    contentRect?: {
        width: number;
        height: number;
        left: number;
        top: number;
    };
}

const props = defineProps<Props>();
const emit = defineEmits(['resize']);

const shapeKey = props.horizontal ? 'width' : 'height';

const [slotRef, rect] = useMeasure({ type: 'throttle', wait: 666 });

watch(
    () => rect[shapeKey],
    () => emit('resize', props.uniqueKey, rect[shapeKey], true),
);

const _outerFixedPosition = computed(() => (props.outerFixedPosition === true ? 0 : props.outerFixedPosition));
const scrollWrapperRef = inject<Ref<HTMLDivElement>>('scrollWrapperRef');
const shepherd = ref<HTMLDivElement>(null!);

const fixedKey: 'height' | 'width' = props.horizontal ? 'height' : 'width';
const fixedDirectionMain = props.horizontal
    ? props.uniqueKey === 'header'
        ? 'left'
        : 'right'
    : props.uniqueKey === 'header'
    ? 'top'
    : 'bottom';
const fixedDirectionSecondary = props.horizontal ? 'top' : 'left';

const isInFixed = ref(false);
const isWrapperIntersecting = ref(false);
let fixedWrapperRef = ref<HTMLDivElement>(null!);

const setFixedNavSize = () => {
    if (props.innerFixed) {
        if (!fixedWrapperRef.value || !slotRef.value) return;
        fixedWrapperRef.value.style[fixedKey] = props.wrapperRect[fixedKey] + 'px';
        slotRef.value.style[fixedKey] = props.contentRect?.[fixedKey] + 'px';
    } else {
        if (!fixedWrapperRef.value) return;
        syncScroll();
        fixedWrapperRef.value.style[fixedKey] = props.wrapperRect[fixedKey] + 'px';
        fixedWrapperRef.value.style[fixedDirectionSecondary] = props.wrapperRect[fixedDirectionSecondary] + 'px';
    }
};

let shepherdIO: IntersectionObserver | null = null;
let wrapperIO: IntersectionObserver | null = null;
const clearIO = () => {
    if (shepherdIO) {
        shepherdIO.disconnect();
        shepherdIO = null;
    }
    if (wrapperIO) {
        wrapperIO.disconnect();
        wrapperIO = null;
    }
};
const navIOCallback: IntersectionObserverCallback = ([entry]) => (isInFixed.value = !entry.isIntersecting);
const wrapperIOCallback: IntersectionObserverCallback = ([entry]) =>
    (isWrapperIntersecting.value = entry.isIntersecting);
watch(
    () => [rect[fixedKey], props.wrapperRect[fixedKey], scrollWrapperRef?.value],
    () => {
        if (
            typeof _outerFixedPosition.value !== 'number' ||
            !scrollWrapperRef?.value?.scrollEl ||
            !shepherd.value ||
            !rect[fixedKey]
        )
            return;
        clearIO();
        wrapperIO = new IntersectionObserver(wrapperIOCallback);
        wrapperIO.observe(scrollWrapperRef.value.scrollEl);
        shepherdIO = new IntersectionObserver(navIOCallback, {
            threshold: [0, clamp(props.wrapperRect[fixedKey] / rect[fixedKey], 0, 1)],
        });
        shepherdIO.observe(shepherd.value);
    },
);
onUnmounted(clearIO);

watch(
    () => [
        isInFixed.value,
        props.wrapperRect[fixedKey],
        props.wrapperRect[fixedDirectionSecondary],
        props.contentRect?.[fixedKey],
    ],
    () => {
        if (typeof _outerFixedPosition.value !== 'number' && props.innerFixed !== true) return;
        setFixedNavSize();
    },
);

const scrollAttr = props.horizontal ? 'scrollTop' : 'scrollLeft';
const syncScroll = () => {
    if (!fixedWrapperRef.value || !scrollWrapperRef?.value) return;
    fixedWrapperRef.value[scrollAttr] =
        scrollWrapperRef.value[scrollAttr] ?? scrollWrapperRef.value.scrollEl?.[scrollAttr];
};

watch(
    () => [isInFixed.value, isWrapperIntersecting.value],
    () => {
        if (isInFixed.value && isWrapperIntersecting.value) nextTick(syncScroll);
    },
);

defineExpose({
    syncScroll,
});
</script>

<style lang="scss" module>
.nav {
    &.isInFixed {
        opacity: 0;
    }
}

.outerFixedWrapper {
    position: fixed;
    overflow: hidden;
    width: fit-content;
    z-index: 20;
    contain: content;
}
.innerFixedWrapper {
    overflow: hidden;
}

.shepherd {
    position: absolute;
    &.vertical {
        width: 100%;
        height: 0;
    }
    &.horizontal {
        width: 0;
        height: 100%;
    }
    contain: strict;
    content-visibility: auto;
}
</style>
